import { useEffect, useState } from "react";
import { api, getActiveBook, Manifest } from "../api";
import { usd } from "../lib/money";
import Desk from "./Desk";
import { StoryChartButton } from "./StoryChart";

// THE BOOK'S PLAN — every sleeve's standing orders in one place. Growth books:
// thesis + weekly equity strikes. Daily books: today's desk plan + proposals.
// Both: the crypto and forex sleeves' weekly strikes. Each "Set strikes"
// action runs for THIS book — the chip in the frame says which.
type Thesis = { version: number; content: string } | null;
type EqStrike = {
  symbol: string; side: string; qty: number;
  entry_strike: number | null; target: number | null; invalidation_stop: number | null;
  rationale: string; price: number | null; held: boolean; armed: boolean; at_market?: boolean;
};
type Watch = { set_at: string | null; narrative: string; strikes: EqStrike[]; last_watch_at: string | null };
type CryptoStrike = {
  symbol: string; entry_strike: number | null; target: number | null;
  invalidation_stop: number | null; qty: number; price: number | null; held: boolean;
};
type CryptoOverview = {
  venue: string; venue_error: string | null; budget: number; deployed: number;
  strikes: CryptoStrike[]; narrative: string; set_at: string | null;
};
type FxStrike = {
  symbol: string; side: string; entry: number; target: number | null;
  invalidation_stop: number | null; notional: number; price: number | null; held: boolean;
};
type FxOverview = {
  venue: string; venue_error: string | null; budget: number; deployed: number;
  tradable_here: boolean; not_tradable_reason: string;
  strikes: FxStrike[]; narrative: string; set_at: string | null;
};

export default function Plan({ manifest }: { manifest: Manifest }) {
  const isGrowth = getActiveBook().endsWith("long_term");
  const [thesis, setThesis] = useState<Thesis>(null);
  const [longRange, setLongRange] = useState<{ current: { version: number; text: string; by?: string; show_day?: string | null;
    pillars?: { name: string; claim: string; watch: string }[] } } | null>(null);
  const [showLongRange, setShowLongRange] = useState(false);
  const [watch, setWatch] = useState<Watch | null>(null);
  const [crypto, setCrypto] = useState<CryptoOverview | null>(null);
  const [fx, setFx] = useState<FxOverview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showThesis, setShowThesis] = useState(false);

  useEffect(() => { void load(); }, []);
  async function load() {
    try {
      if (isGrowth) {
        setThesis(await api.get<Thesis>("/api/longterm/thesis"));
        try { setLongRange(await api.get<typeof longRange>("/api/longterm/long-range")); } catch { /* optional */ }
        setWatch(await api.get<Watch>("/api/longterm/watchlist"));
      }
      setCrypto(await api.get<CryptoOverview>("/api/crypto/overview"));
      setFx(await api.get<FxOverview>("/api/forex/overview"));
      setErr(null);
    } catch (e) { setErr((e as Error).message); }
  }
  async function run(kind: "equity" | "crypto" | "forex") {
    setBusy(kind); setErr(null);
    const path = kind === "equity" ? "/api/longterm/run-weekly"
      : kind === "crypto" ? "/api/crypto/run-weekly" : "/api/forex/run-weekly";
    try { await api.post(path); await load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h1 className="text-lg font-bold">Plan <span className="text-gold">{isGrowth ? "(Growth)" : "(Daily)"}</span></h1>
        <span className="text-xs text-slate-500">
          every sleeve&rsquo;s standing orders for this book — set weekly, executed by the watch when price hits
        </span>
      </div>
      {err && <div className="text-down text-sm">{err}</div>}

      {/* Growth: the hundred-year view the thesis sits inside */}
      {isGrowth && longRange?.current && (
        <div className="card">
          <div className="flex items-center gap-2">
            <h2 className="label mb-0">The long view</h2>
            <span className="badge bg-gold/20 text-gold">v{longRange.current.version}</span>
            {longRange.current.show_day && <span className="text-xs text-slate-500">revised {longRange.current.show_day} by {longRange.current.by}</span>}
            <button className="ml-auto text-xs text-slate-400 hover:underline" onClick={() => setShowLongRange((v) => !v)}>{showLongRange ? "hide" : "read"}</button>
          </div>
          <ul className="text-sm text-slate-300 mt-2 space-y-1">
            {(longRange.current.pillars ?? []).map((p) => <li key={p.name}><span className="text-gold">{p.name}.</span> {p.claim} <span className="text-slate-500">Watch: {p.watch}</span></li>)}
          </ul>
          {showLongRange && <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans mt-3">{longRange.current.text}</pre>}
        </div>
      )}
      {/* Growth: the thesis the strikes serve */}
      {isGrowth && thesis && (
        <div className="card">
          <button className="w-full text-left flex items-center gap-2" onClick={() => setShowThesis((s) => !s)}>
            <h2 className="label mb-0">Long-term thesis</h2>
            <span className="badge bg-gold/20 text-gold">v{thesis.version}</span>
            <span className="ml-auto text-slate-500 text-sm">{showThesis ? "hide" : "show"}</span>
          </button>
          {showThesis && <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans mt-2">{thesis.content}</pre>}
        </div>
      )}

      {/* Daily: today's desk (plan → proposals → rulings) */}
      {!isGrowth && <Desk manifest={manifest} />}

      {/* Equity strikes (growth books only — the daily book's equities are desk trades) */}
      {isGrowth && (
        <SleeveCard title="Equity strikes" sub={watch?.narrative}
                    setAt={watch?.set_at ?? null}
                    action={<RunBtn label="Set equity strikes" busy={busy === "equity"} onClick={() => run("equity")} />}>
          <StrikeTable
            cols={["Symbol", "Side", "Qty", "Now", "Buy ≤", "Target", "Stop", "Status", ""]}
            rows={(watch?.strikes ?? []).map((s) => [
              <b key="s">{s.symbol}</b>, s.side, s.qty,
              s.price != null ? `$${s.price.toFixed(2)}` : "—",
              (s.at_market || (s.price != null && s.entry_strike != null && s.side === "long" && s.entry_strike >= s.price))
                ? <span key="m" className="text-accent">at market</span>
                : s.entry_strike != null ? `$${s.entry_strike}` : "—",
              <span key="t" className="text-gold">{s.target != null ? `$${s.target}` : "—"}</span>,
              <span key="x" className="text-down">{s.invalidation_stop != null ? `$${s.invalidation_stop}` : "—"}</span>,
              <Status key="st" held={s.held} armed={s.armed} />,
              <StoryChartButton key="c" symbol={s.symbol} entry={s.entry_strike} stop={s.invalidation_stop} target={s.target} side={s.side} label="strike" />,
            ])}
            empty="no equity strikes — run the weekly session" />
        </SleeveCard>
      )}

      {/* Crypto sleeve strikes */}
      <SleeveCard title="Crypto strikes" sub={crypto?.narrative}
                  setAt={crypto?.set_at ?? null}
                  meta={crypto ? `${crypto.venue.toUpperCase()} · budget ${usd(crypto.budget)} · deployed ${usd(crypto.deployed)}` : undefined}
                  warn={crypto?.venue_error}
                  action={<RunBtn label="Set crypto strikes" busy={busy === "crypto"} onClick={() => run("crypto")} />}>
        <StrikeTable
          cols={["Pair", "Now", "Buy ≤", "Target", "Stop", "Qty", "Status", ""]}
          rows={(crypto?.strikes ?? []).map((s) => [
            <b key="s">{s.symbol}</b>,
            s.price ?? "—", s.entry_strike ?? "—",
            <span key="t" className="text-gold">{s.target ?? "—"}</span>,
            <span key="x" className="text-down">{s.invalidation_stop ?? "—"}</span>,
            s.qty, <Status key="st" held={s.held} />,
            <StoryChartButton key="c" symbol={s.symbol} entry={s.entry_strike} stop={s.invalidation_stop} target={s.target} side="long" label="strike" />,
          ])}
          empty="no crypto strikes — run the weekly session" />
      </SleeveCard>

      {/* Forex sleeve strikes */}
      <SleeveCard title="Forex strikes" sub={fx?.narrative}
                  setAt={fx?.set_at ?? null}
                  meta={fx ? `${fx.venue.toUpperCase()} · budget ${usd(fx.budget)} · deployed ${usd(fx.deployed)}` : undefined}
                  warn={fx && !fx.tradable_here ? `not tradable in this book — ${fx.not_tradable_reason}` : fx?.venue_error}
                  action={fx?.tradable_here
                    ? <RunBtn label="Set forex strikes" busy={busy === "forex"} onClick={() => run("forex")} />
                    : undefined}>
        <StrikeTable
          cols={["Pair", "Side", "Now", "Entry", "Target", "Stop", "Size", "Status"]}
          rows={(fx?.strikes ?? []).map((s) => [
            <b key="s">{s.symbol}</b>,
            <span key="d" className={s.side === "long" ? "text-up" : "text-down"}>{s.side}</span>,
            s.price ?? "—", s.entry,
            <span key="t" className="text-gold">{s.target ?? "—"}</span>,
            <span key="x" className="text-down">{s.invalidation_stop ?? "—"}</span>,
            usd(s.notional), <Status key="st" held={s.held} />,
          ])}
          empty="no forex strikes" />
      </SleeveCard>
    </div>
  );
}

function SleeveCard({ title, sub, setAt, meta, warn, action, children }: {
  title: string; sub?: string; setAt: string | null; meta?: string;
  warn?: string | null; action?: JSX.Element; children: JSX.Element;
}) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <h2 className="label mb-0">{title}</h2>
        {meta && <span className="text-[11px] text-slate-500 font-mono">{meta}</span>}
        {setAt && <span className="text-[11px] text-slate-600">set {new Date(setAt).toLocaleString()}</span>}
        <span className="ml-auto">{action}</span>
      </div>
      {warn && <div className="text-xs text-gold mb-2">{warn}</div>}
      {sub && <p className="text-sm text-slate-400 mb-2">{sub}</p>}
      {children}
    </div>
  );
}

function RunBtn({ label, busy, onClick }: { label: string; busy: boolean; onClick: () => void }) {
  return (
    <button className="btn btn-primary text-xs" onClick={onClick} disabled={busy}>
      {busy ? "Stonky setting strikes…" : label}
    </button>
  );
}

function Status({ held, armed }: { held: boolean; armed?: boolean }) {
  if (held) return <span className="badge bg-gold/20 text-gold">held</span>;
  if (armed) return <span className="badge bg-up/20 text-up">armed</span>;
  return <span className="badge bg-slate-700 text-slate-400">watching</span>;
}

// Compact table that collapses to stacked cards on phones.
function StrikeTable({ cols, rows, empty }: {
  cols: string[]; rows: (string | number | JSX.Element)[][]; empty: string;
}) {
  if (rows.length === 0) return <div className="text-slate-600 text-sm">{empty}</div>;
  return (
    <>
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 uppercase">
            {cols.map((c) => <th key={c} className="py-1 pr-2">{c}</th>)}
          </tr></thead>
          <tbody className="font-mono">
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-edge">
                {r.map((cell, j) => <td key={j} className="py-1.5 pr-2">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sm:hidden space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="rounded-lg border border-edge p-2 text-sm font-mono">
            <div className="flex justify-between items-center mb-1">{r[0]}{r[r.length - 1]}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
              {r.slice(1, -1).map((cell, j) => (
                <div key={j} className="flex justify-between">
                  <span className="text-slate-500 font-sans">{cols[j + 1]}</span><span>{cell}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
